import React, { memo, useState, useCallback, useMemo, useRef } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Switch,
  Chip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { format, isValid, parse, getDay } from 'date-fns';
import type { DailyShiftSlotGrouped } from '../../../../domain/dailyShiftSlot';
import type { IncentiveSettingInput } from '../../../../domain/dailyShiftSlot';
import {
  IncentiveSettingPolicy,
  MIN_INCENTIVE_AMOUNT,
  MAX_INCENTIVE_AMOUNT,
  MIN_WORKING_HOURS,
  MAX_WORKING_HOURS,
} from '../../../../domain/dailyShiftSlot';
import { ModalContent } from '../MonthlyShiftSlotSetup.styles';
import UrgentSettingsSlotTable from './UrgentSettingsSlotTable';
import type { SlotTableRow } from './UrgentSettingsSlotTable';

/**
 * 急募設定モーダルのProps
 */
export interface UrgentSettingsModalProps {
  /** モーダルの開閉状態 */
  open: boolean;
  /** 対象のスロットデータ */
  slots: DailyShiftSlotGrouped[];
  /** 対象年 */
  year: number;
  /** 対象月 */
  month: number;
  /** 保存時のコールバック */
  onSave: (settings: IncentiveSettingInput) => void;
  /** 閉じる時のコールバック */
  onClose: () => void;
}

/**
 * 急募設定フォームの状態
 */
export interface UrgentSettingsFormState {
  /** インセンティブ金額（円）※入力途中の空欄を許容するため文字列で保持 */
  incentiveAmount: string;
  /** 当日最低勤務時間（時間）※入力途中の空欄を許容するため文字列で保持 */
  minimumWorkingHours: string;
  /** 開始日（YYYY-MM-DD形式） */
  startDate: string;
  /** 終了日（YYYY-MM-DD形式） */
  endDate: string;
  /** 選択されたスロットIDの配列 */
  selectedSlotIds: string[];
  /** 保存時にLINE通知を送信するか */
  sendLineNotification: boolean;
  /** LINE通知文面（変数は送信時に実値へ置換） */
  notificationMessage: string;
}

/**
 * フォームエラーの状態
 */
interface FormErrors {
  /** インセンティブ金額のエラー */
  incentiveAmount?: string;
  /** 最低勤務時間のエラー */
  minimumWorkingHours?: string;
  /** 日付範囲のエラー */
  dateRange?: string;
  /** スロット選択のエラー */
  slotSelection?: string;
}

/**
 * LINE通知のデフォルト文面
 * {} で囲まれた変数は送信時に実際の値へ自動置換される
 */
const DEFAULT_NOTIFICATION_MESSAGE = `【急募のお知らせ】

診療日：{診療日時}
診療科：{診療科}
インセンティブ：{インセンティブ額}

ご都合のつく先生はアプリから応募をお願いします。
締切：{募集締切}

▶ 応募はこちら
{応募URL}`;

/**
 * 通知文面に挿入できる変数一覧
 */
const NOTIFICATION_VARIABLES = [
  '{診療日時}',
  '{診療科}',
  '{インセンティブ額}',
  '{募集締切}',
  '{応募URL}',
  '{クリニック名}',
] as const;

/**
 * 初期フォーム状態（hasUnsavedChanges計算用）
 */
const initialFormState: UrgentSettingsFormState = {
  incentiveAmount: '0',
  minimumWorkingHours: '1',
  startDate: '',
  endDate: '',
  selectedSlotIds: [],
  sendLineNotification: true,
  notificationMessage: DEFAULT_NOTIFICATION_MESSAGE,
};

/**
 * 曜日名を取得
 */
const DAY_OF_WEEK_NAMES = ['日', '月', '火', '水', '木', '金', '土'];

/**
 * DailyShiftSlotGrouped配列からSlotTableRow配列への変換
 *
 * 時間枠単位に展開し、不足枠が1以上の枠のみを表示対象とする
 * @param slots - 変換元のスロットグループ配列
 * @param startDate - フィルタリング開始日（YYYY-MM-DD形式、空の場合はフィルタリングなし）
 * @param endDate - フィルタリング終了日（YYYY-MM-DD形式、空の場合はフィルタリングなし）
 * @returns SlotTableRow配列
 */
const convertToSlotTableRows = (
  slots: DailyShiftSlotGrouped[],
  startDate: string,
  endDate: string
): SlotTableRow[] => {
  // 日付範囲でフィルタリング
  const filteredSlots = slots.filter((slot) => {
    const slotDate = slot.date;
    if (startDate && slotDate < startDate) return false;
    if (endDate && slotDate > endDate) return false;
    return true;
  });

  // 時間枠単位に展開してSlotTableRow形式に変換
  return filteredSlots.flatMap((slot) => {
    // 日付から曜日を取得
    const dateObj = parse(slot.date, 'yyyy-MM-dd', new Date());
    const dayOfWeek = isValid(dateObj) ? getDay(dateObj) : slot.dayOfWeek;
    const dayOfWeekName = DAY_OF_WEEK_NAMES[dayOfWeek];

    // 表示用日付を生成（YYYY年M月D日（曜日））
    const dateLabel = isValid(dateObj)
      ? `${format(dateObj, 'yyyy年M月d日')}（${dayOfWeekName}）`
      : slot.date;

    return slot.slots
      .filter((timeSlot) => timeSlot.shortageSlots >= 1)
      .map((timeSlot) => ({
        id: timeSlot.id,
        date: slot.date,
        displayDateTime: `${dateLabel} ${timeSlot.startTime}〜${timeSlot.endTime}`,
        recruitmentSlots: timeSlot.recruitmentSlots,
        urgentSlots: timeSlot.urgentSlots,
        confirmedSlots: timeSlot.confirmedSlots,
        shortageSlots: timeSlot.shortageSlots,
        isUrgentSet: timeSlot.urgentSlots >= 1,
      }));
  });
};

/**
 * 急募設定モーダルコンポーネント
 *
 * - タイトル: 「急募設定」+ サブタイトル
 * - インセンティブ金額・当日最低勤務時間・期間指定
 * - 対象シフト枠の選択（不足枠のある時間枠のみ表示）
 * - LINE通知（送信トグル・通知文面編集・変数挿入）
 * - キャンセル・保存ボタン
 */
const UrgentSettingsModal: React.FC<UrgentSettingsModalProps> = memo(({
  open,
  slots,
  year,
  month,
  onSave,
  onClose,
}) => {
  // フォーム状態管理
  const [formState, setFormState] = useState<UrgentSettingsFormState>({
    incentiveAmount: '0',
    minimumWorkingHours: '1',
    startDate: '',
    endDate: '',
    selectedSlotIds: [],
    sendLineNotification: true,
    notificationMessage: DEFAULT_NOTIFICATION_MESSAGE,
  });

  // 通知文面入力欄への参照（変数挿入時のカーソル位置取得用）
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);

  // エラー状態管理
  const [errors, setErrors] = useState<FormErrors>({});

  // 閉じる確認ダイアログの表示状態
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);

  /**
   * 未保存の変更があるかどうかを計算
   */
  const hasUnsavedChanges = useMemo(() => {
    return (
      formState.incentiveAmount !== initialFormState.incentiveAmount ||
      formState.minimumWorkingHours !== initialFormState.minimumWorkingHours ||
      formState.startDate !== initialFormState.startDate ||
      formState.endDate !== initialFormState.endDate ||
      formState.selectedSlotIds.length !== initialFormState.selectedSlotIds.length ||
      formState.sendLineNotification !== initialFormState.sendLineNotification ||
      formState.notificationMessage !== initialFormState.notificationMessage
    );
  }, [formState]);

  /**
   * フォームバリデーション
   * @returns バリデーションが成功した場合はtrue
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const incentiveAmountValue =
      formState.incentiveAmount === '' ? NaN : Number(formState.incentiveAmount);
    if (!IncentiveSettingPolicy.isValidIncentiveAmount(incentiveAmountValue)) {
      newErrors.incentiveAmount = `インセンティブ金額は${MIN_INCENTIVE_AMOUNT}〜${MAX_INCENTIVE_AMOUNT}円の整数で入力してください`;
    }

    const workingHoursValue =
      formState.minimumWorkingHours === '' ? NaN : Number(formState.minimumWorkingHours);
    if (!IncentiveSettingPolicy.isValidWorkingHours(workingHoursValue)) {
      newErrors.minimumWorkingHours = `勤務時間は${MIN_WORKING_HOURS}〜${MAX_WORKING_HOURS}時間の整数で入力してください`;
    }

    // 日付が両方入力されている場合のみ日付範囲を検証
    if (formState.startDate && formState.endDate) {
      if (!IncentiveSettingPolicy.isValidDateRange(formState.startDate, formState.endDate)) {
        newErrors.dateRange = '開始日は終了日以前の日付を選択してください';
      }
    }

    // スロット選択のバリデーション
    if (formState.selectedSlotIds.length === 0) {
      newErrors.slotSelection = '対象枠を1つ以上選択してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState]);

  /**
   * インセンティブ金額変更ハンドラー
   */
  const handleIncentiveAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // 数字以外を除去し、先頭ゼロも自動除去する（"0" 単体・空欄は許容）
      const value = event.target.value
        .replace(/[^0-9]/g, '')
        .replace(/^0+(?=\d)/, '');
      setFormState((prev) => ({ ...prev, incentiveAmount: value }));
    },
    []
  );

  /**
   * 当日最低勤務時間変更ハンドラー
   */
  const handleWorkingHoursChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // 数字以外を除去し、先頭ゼロも自動除去する（"0" 単体・空欄は許容）
      const value = event.target.value
        .replace(/[^0-9]/g, '')
        .replace(/^0+(?=\d)/, '');
      setFormState((prev) => ({ ...prev, minimumWorkingHours: value }));
    },
    []
  );

  /**
   * 開始日変更ハンドラー
   */
  const handleStartDateChange = useCallback((date: Date | null) => {
    setFormState((prev) => ({
      ...prev,
      startDate: date && isValid(date) ? format(date, 'yyyy-MM-dd') : '',
    }));
  }, []);

  /**
   * 終了日変更ハンドラー
   */
  const handleEndDateChange = useCallback((date: Date | null) => {
    setFormState((prev) => ({
      ...prev,
      endDate: date && isValid(date) ? format(date, 'yyyy-MM-dd') : '',
    }));
  }, []);

  /**
   * スロット選択変更ハンドラー
   */
  const handleSlotSelectionChange = useCallback((selectedIds: string[]) => {
    setFormState((prev) => ({
      ...prev,
      selectedSlotIds: selectedIds,
    }));
    // スロット選択時にスロット選択エラーをクリア
    setErrors((prev) => ({ ...prev, slotSelection: undefined }));
  }, []);

  /**
   * LINE通知送信トグル変更ハンドラー
   */
  const handleSendLineNotificationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({ ...prev, sendLineNotification: event.target.checked }));
    },
    []
  );

  /**
   * 通知文面変更ハンドラー
   */
  const handleNotificationMessageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormState((prev) => ({ ...prev, notificationMessage: event.target.value }));
    },
    []
  );

  /**
   * 変数チップクリックハンドラー
   * 通知文面のカーソル位置に変数を挿入する
   */
  const handleInsertVariable = useCallback((variable: string) => {
    const input = messageInputRef.current;
    setFormState((prev) => {
      const message = prev.notificationMessage;
      const start = input?.selectionStart ?? message.length;
      const end = input?.selectionEnd ?? message.length;
      return {
        ...prev,
        notificationMessage: `${message.slice(0, start)}${variable}${message.slice(end)}`,
      };
    });
    // 挿入した変数の直後にカーソルを移動
    if (input) {
      const caretPosition = (input.selectionStart ?? input.value.length) + variable.length;
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(caretPosition, caretPosition);
      });
    }
  }, []);

  /**
   * 閉じるボタンクリックハンドラー
   * 未保存の変更がある場合は確認ダイアログを表示
   */
  const handleCloseClick = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmDialog(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  /**
   * 閉じる確認ダイアログでキャンセル
   */
  const handleCloseConfirmCancel = useCallback(() => {
    setShowCloseConfirmDialog(false);
  }, []);

  /**
   * 閉じる確認ダイアログで確認（閉じる）
   */
  const handleCloseConfirm = useCallback(() => {
    setShowCloseConfirmDialog(false);
    onClose();
  }, [onClose]);

  /**
   * 保存ボタンクリックハンドラー
   * バリデーション成功時にonSaveコールバックを呼び出す
   */
  const handleSave = useCallback(() => {
    if (validateForm()) {
      onSave({
        year,
        month,
        incentiveAmount: Number(formState.incentiveAmount),
        minimumWorkingHours: Number(formState.minimumWorkingHours),
        startDate: formState.startDate,
        endDate: formState.endDate,
        slotIds: formState.selectedSlotIds,
        sendLineNotification: formState.sendLineNotification,
        notificationMessage: formState.notificationMessage,
      });
    }
  }, [validateForm, onSave, year, month, formState]);

  /**
   * 文字列から Date オブジェクトに変換
   */
  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : null;
  };

  /**
   * 日付範囲でフィルタリングされたスロットテーブル行
   */
  const slotTableRows = useMemo(() => {
    return convertToSlotTableRows(slots, formState.startDate, formState.endDate);
  }, [slots, formState.startDate, formState.endDate]);

  return (
    <>
      <Modal open={open} onClose={handleCloseClick}>
        <ModalContent sx={{ minWidth: 800, maxWidth: 900 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                急募設定
              </Typography>
              <Typography variant="body2" color="text.secondary">
                インセンティブを設定し、対象シフト枠を選択してください
              </Typography>
            </Box>
            <Button
              onClick={handleCloseClick}
              size="small"
              sx={{ minWidth: 'auto' }}
            >
              <CloseIcon />
            </Button>
          </Box>

        {/* Content area */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* インセンティブ金額 */}
            <TextField
              label="インセンティブ金額（円）"
              type="text"
              value={formState.incentiveAmount}
              onChange={handleIncentiveAmountChange}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              placeholder={`${MIN_INCENTIVE_AMOUNT}〜${MAX_INCENTIVE_AMOUNT}`}
              error={!!errors.incentiveAmount}
              helperText={errors.incentiveAmount}
              fullWidth
            />

            {/* 当日最低勤務時間 */}
            <TextField
              label="当日最低勤務時間（時間）"
              type="text"
              value={formState.minimumWorkingHours}
              onChange={handleWorkingHoursChange}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              placeholder={`${MIN_WORKING_HOURS}〜${MAX_WORKING_HOURS}`}
              error={!!errors.minimumWorkingHours}
              helperText={errors.minimumWorkingHours}
              fullWidth
            />

            {/* 開始日・終了日 */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label="開始日"
                value={parseDate(formState.startDate)}
                onChange={handleStartDateChange}
                slotProps={{ textField: { fullWidth: true } }}
                format="yyyy/MM/dd"
              />
              <DatePicker
                label="終了日"
                value={parseDate(formState.endDate)}
                onChange={handleEndDateChange}
                slotProps={{ textField: { fullWidth: true } }}
                format="yyyy/MM/dd"
              />
            </Box>

            {/* 日付範囲エラー表示 */}
            {errors.dateRange && (
              <Typography color="error" variant="caption">
                {errors.dateRange}
              </Typography>
            )}

            {/* スロットテーブル */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                対象シフト枠の選択
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                不足枠のあるシフトを表示しています。チェックした枠を急募として設定します。
              </Alert>
              {errors.slotSelection && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                  {errors.slotSelection}
                </Typography>
              )}
              <UrgentSettingsSlotTable
                rows={slotTableRows}
                selectedIds={formState.selectedSlotIds}
                onSelectionChange={handleSlotSelectionChange}
              />
            </Box>

            {/* LINE通知 */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                LINE通知
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  保存時にLINE通知を送信する
                </Typography>
                <Switch
                  checked={formState.sendLineNotification}
                  onChange={handleSendLineNotificationChange}
                />
              </Box>
              <Alert severity="info" sx={{ mb: 1 }}>
                変数は送信時に実際の値へ自動置換されます。複数枠を選択している場合、枠ごとに個別に送信されます。
              </Alert>
              <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip label="LINE" color="success" size="small" />
                  <Typography variant="caption" color="text.secondary">
                    通知文面（クリックして編集）
                  </Typography>
                </Box>
                <TextField
                  multiline
                  minRows={8}
                  fullWidth
                  value={formState.notificationMessage}
                  onChange={handleNotificationMessageChange}
                  inputRef={messageInputRef}
                  sx={{ bgcolor: 'background.paper' }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {NOTIFICATION_VARIABLES.map((variable) => (
                    <Chip
                      key={variable}
                      label={variable}
                      size="small"
                      variant="outlined"
                      onClick={() => handleInsertVariable(variable)}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  ↑ 変数をクリックするとカーソル位置に挿入されます
                </Typography>
              </Box>
            </Box>
          </Box>
        </LocalizationProvider>

          {/* Footer */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseClick}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
            >
              保存
            </Button>
          </Box>
        </ModalContent>
      </Modal>

      {/* 閉じる確認ダイアログ */}
      <Dialog
        open={showCloseConfirmDialog}
        onClose={handleCloseConfirmCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            確認
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            変更が保存されていません。閉じてもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={handleCloseConfirmCancel}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCloseConfirm}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

UrgentSettingsModal.displayName = 'UrgentSettingsModal';

export default UrgentSettingsModal;
